import React, { useState } from 'react';
import { X, Heart, Smile, Meh, Frown, Coffee, Users, BookOpen, Music } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const DailyCheckInModal = ({ isOpen, onClose, onComplete }) => {
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [checkInData, setCheckInData] = useState({
    mood: null,
    energy: 5,
    sleep: 7,
    activities: [],
    gratitude: '',
    goal_for_today: ''
  });

  if (!isOpen) return null;

  const moods = [
    { value: 'excellent', score: 1.0, icon: Smile, label: 'Excellent', color: 'green' },
    { value: 'good', score: 0.75, icon: Smile, label: 'Good', color: 'blue' },
    { value: 'okay', score: 0.5, icon: Meh, label: 'Okay', color: 'yellow' },
    { value: 'sad', score: 0.25, icon: Frown, label: 'Sad', color: 'orange' },
    { value: 'terrible', score: 0.1, icon: Frown, label: 'Terrible', color: 'red' }
  ];

  const activities = [
    { id: 'exercise', icon: Coffee, label: 'Exercise' },
    { id: 'social', icon: Users, label: 'Social Time' },
    { id: 'reading', icon: BookOpen, label: 'Reading' },
    { id: 'music', icon: Music, label: 'Music' }
  ];

  const handleSubmit = async () => {
    // Find mood score
    const moodEntry = moods.find(m => m.value === checkInData.mood);

    const submitData = {
      mood_score: moodEntry?.score || 0.5,
      mood_label: moodEntry?.label || 'Okay',
      gratitude: checkInData.gratitude,
      goal_for_today: checkInData.goal_for_today
    };

    // Call the parent's onComplete handler with the data
    if (onComplete) {
      await onComplete(submitData);
    }

    // Reset form
    setStep(1);
    setCheckInData({
      mood: null,
      energy: 5,
      sleep: 7,
      activities: [],
      gratitude: '',
      goal_for_today: ''
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Daily Check-In</h2>
            <p className="text-sm text-gray-600 mt-1">Step {step} of 3</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">How are you feeling today?</h3>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                  {moods.map((mood) => {
                    const Icon = mood.icon;
                    return (
                      <button
                        key={mood.value}
                        onClick={() => setCheckInData({ ...checkInData, mood: mood.value })}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          checkInData.mood === mood.value
                            ? `border-${mood.color}-500 bg-${mood.color}-50`
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon size={32} className={`mx-auto mb-2 text-${mood.color}-500`} />
                        <p className="text-xs font-medium text-gray-700">{mood.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Energy Level: {checkInData.energy}/10
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={checkInData.energy}
                  onChange={(e) => setCheckInData({ ...checkInData, energy: parseInt(e.target.value) })}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Hours of Sleep: {checkInData.sleep} hours
                </label>
                <input
                  type="range"
                  min="0"
                  max="12"
                  value={checkInData.sleep}
                  onChange={(e) => setCheckInData({ ...checkInData, sleep: parseInt(e.target.value) })}
                  className="w-full"
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">What did you do today?</h3>
                <div className="grid grid-cols-2 gap-3">
                  {activities.map((activity) => {
                    const Icon = activity.icon;
                    const isSelected = checkInData.activities.includes(activity.id);
                    return (
                      <button
                        key={activity.id}
                        onClick={() => {
                          const newActivities = isSelected
                            ? checkInData.activities.filter(a => a !== activity.id)
                            : [...checkInData.activities, activity.id];
                          setCheckInData({ ...checkInData, activities: newActivities });
                        }}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon size={24} className={`mx-auto mb-2 ${isSelected ? 'text-blue-500' : 'text-gray-400'}`} />
                        <p className="text-sm font-medium text-gray-700">{activity.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What are you grateful for today?
                </label>
                <textarea
                  value={checkInData.gratitude}
                  onChange={(e) => setCheckInData({ ...checkInData, gratitude: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="I'm grateful for..."
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Set your intention for today</h3>
                <textarea
                  value={checkInData.goal_for_today}
                  onChange={(e) => setCheckInData({ ...checkInData, goal_for_today: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="What's one thing you'd like to accomplish today?"
                />
              </div>

              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <Heart size={20} className="text-green-600" fill="currentColor" />
                  <p className="font-semibold text-green-900">Reward: +5 Hearts (bonus for milestones!)</p>
                </div>
                <p className="text-sm text-green-700">Complete your check-in to earn hearts and maintain your streak!</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex items-center justify-between">
          {step > 1 && (
            <button
              onClick={() => setStep(step - 1)}
              className="px-6 py-2 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Back
            </button>
          )}
          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 && !checkInData.mood}
              className="ml-auto px-6 py-2 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="ml-auto px-6 py-2 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors"
            >
              Complete Check-In
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyCheckInModal;
